import {useEffect, useState} from 'react';
import {Modal, Spinner} from '@tyrannosaurustech/ui';

import {useGetUsersQuery} from '../../../redux/reduxQuery/usersApi';
import {useGetVettingConfigQuery, useUpdateVettingConfigMutation} from '../../../redux/reduxQuery/vettingApi';
import {useMutationWrapper} from '../../../common/api';
import {VettingConfig, statusLabel} from './constants';

const SectionTitle = ({title, hint}: {title: string; hint?: string}) => (
  <div className="mb-2 mt-5 first:mt-0">
    <div className="text-sm font-bold">{title}</div>
    {hint && <div className="text-xs text-gray-500">{hint}</div>}
  </div>
);

const GOAL_FIELDS: {key: 'daily' | 'weekly' | 'monthly'; label: string}[] = [
  {key: 'daily', label: 'Per day'},
  {key: 'weekly', label: 'Per week'},
  {key: 'monthly', label: 'Per month'},
];

export const ConfigModal = ({isOpen, onClose}: {isOpen: boolean; onClose: () => void}) => {
  const {data: configData} = useGetVettingConfigQuery(undefined, {
    skip: !isOpen,
  });
  const {data: admins} = useGetUsersQuery('roles=admin', {skip: !isOpen});
  const [updateConfig] = useUpdateVettingConfigMutation();
  const [isSaving, setIsSaving] = useState(false);

  const [trackedUserIds, setTrackedUserIds] = useState<string[]>([]);
  const [goals, setGoals] = useState({daily: 0, weekly: 0, monthly: 0});
  const [completedStatus, setCompletedStatus] = useState('vetted');
  const [pendingStatuses, setPendingStatuses] = useState<string[]>([]);

  const config: VettingConfig | undefined = configData?.config;
  const statuses: string[] = configData?.statuses || [];

  useEffect(() => {
    if (isOpen && config) {
      setTrackedUserIds(config.trackedUserIds);
      setGoals(config.goals);
      setCompletedStatus(config.completedStatus);
      setPendingStatuses(config.pendingStatuses);
    }
  }, [isOpen, config]);

  const save = useMutationWrapper(updateConfig, 'Vetting settings saved', () => {
    setIsSaving(false);
    onClose();
  });

  const onConfirm = () => {
    setIsSaving(true);
    save({
      trackedUserIds,
      goals,
      completedStatus,
      pendingStatuses: pendingStatuses.filter((s) => s !== completedStatus),
    }).catch(() => setIsSaving(false));
  };

  const toggle = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  return (
    <Modal
      swapButtons
      onConfirm={onConfirm}
      open={isOpen}
      title="Vetting Settings"
      confirmText="Save Changes"
      setOpen={onClose}
      loading={isSaving}
      buttonAlignment="right"
      titleClassName="text-left font-bold text-xl font-semibold not-italic capitalize"
      panelClassName="w-[600px] max-w-[1000px] max-h-[85vh] overflow-y-auto"
    >
      {!config ? (
        <div className="flex justify-center py-10">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <div className="mt-2 mb-2 text-left">
          <SectionTitle title="Vetting team" hint="Whose completed and outstanding work is tracked on this dashboard." />
          <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 p-1">
            {(admins || []).map((admin) => (
              <label key={admin.id} className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-purple-700"
                  checked={trackedUserIds.includes(admin.id)}
                  onChange={() => setTrackedUserIds(toggle(trackedUserIds, admin.id))}
                />
                <span className="text-sm font-medium">{admin.name}</span>
                <span className="ml-auto text-xs text-gray-400">{admin.email}</span>
              </label>
            ))}
            {!admins?.length && <div className="px-3 py-2 text-sm text-gray-400">No admin users found.</div>}
          </div>

          <SectionTitle title="Goals" hint="Completed vettings the team should reach per period. 0 means no goal." />
          <div className="flex gap-3">
            {GOAL_FIELDS.map(({key, label}) => (
              <label key={key} className="flex-1 text-xs font-semibold text-gray-500">
                {label}
                <input
                  type="number"
                  min={0}
                  value={goals[key]}
                  onChange={(e) =>
                    setGoals({
                      ...goals,
                      [key]: Math.max(0, Number(e.target.value) || 0),
                    })
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 outline-none focus:border-purple-700"
                />
              </label>
            ))}
          </div>

          <SectionTitle title="Completed status" hint="Moving a request into this status counts as a completed vetting." />
          <select
            value={completedStatus}
            onChange={(e) => setCompletedStatus(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-purple-700"
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </select>

          <SectionTitle title="Outstanding statuses" hint="Requests currently in these statuses count as pending vetting work." />
          <div className="grid grid-cols-2 gap-1">
            {statuses
              .filter((status) => status !== completedStatus)
              .map((status) => (
                <label key={status} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-purple-700"
                    checked={pendingStatuses.includes(status)}
                    onChange={() => setPendingStatuses(toggle(pendingStatuses, status))}
                  />
                  <span className="text-sm">{statusLabel(status)}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </Modal>
  );
};
